import { Badge, Button, message, Modal, Tabs } from "antd";
import moment from "moment";
import { useEffect, useMemo, useState } from "react";
import type { Key } from "react";
import type { NotificationVo, UserMessage } from "@zhihaoscm/entity";
import {
    ADMIN_PUBLISH_PLAT,
    NOTIFICATION_TYPE,
    PRODUCT_TYPE_PRICE_WARNING_QUERY_TYPE,
    PURCHASE_GOODS_TYPE,
    USER_MESSAGE_STATE,
    USER_MESSAGE_TYPE,
    UserMessageConstants
} from "@zhihaoscm/meta";
import { ProTable, type PageParams, type ProColumn } from "@/components/common";
import { notificationService } from "@/api/notificationService";
import { userMessageService } from "@/api/userMessage";
import { indexService } from "@/api/indexService";

type MessageRow = UserMessage | NotificationVo;
const isNotification = (row: MessageRow): row is NotificationVo => "notification" in row;
/** 按旧消息码生成 React/外部业务目标；无目标的消息只执行已读。 */
export function resolveMessageTarget(row: UserMessage): string | undefined {
    const id = row.detailId;
    const queryId = `?id=${id}`;
    const initiator = Boolean(row.initiator);
    const targets: Partial<Record<UserMessageConstants, string>> = {
        [UserMessageConstants.INTEND_DETAIL_PAGE]: `/console/intend/detail${queryId}`,
        [UserMessageConstants.PROJECT_DETAIL_PAGE]: `/console/contract/detail${queryId}`,
        [UserMessageConstants.STORE_DETAIL_PAGE]: `/console/project/detail${queryId}`,
        [UserMessageConstants.RECEIPT_DETAIL_PAGE]: `/console/payment/detail${queryId}&payType=Collect`,
        [UserMessageConstants.PURCHASE_GOOD_DETAIL_PAGE]: `/console/purchaseGoods/detail${queryId}&purGoodsType=${initiator ? "My" : "Down"}`,
        [UserMessageConstants.SIGN_IN_DETAIL_PAGE]: `/console/purchaseGoodsReceipt/detail${queryId}&purGoodsReceiptType=${initiator ? "Down" : "Up"}`,
        [UserMessageConstants.ACCOUNT_DETAIL_PAGE]: `/console/reconciliation/detail${queryId}&type=${initiator ? PURCHASE_GOODS_TYPE.ONE.code : PURCHASE_GOODS_TYPE.TWO.code}`,
        [UserMessageConstants.OPEN_INVOICE_DETAIL_PAGE]: `/console/invoice/detail${queryId}&type=${initiator}`,
        [UserMessageConstants.FIND_BOAT_DETAIL_PAGE]: `/logistics/shipOrder/detail?requireId=${id}`,
        [UserMessageConstants.TRANSFORM_BILL_DETAIL_PAGE]: `/logistics/shipOrder/detail${queryId}`,
        [UserMessageConstants.MEMBER_DETAIL_PAGE]: "/me/member",
        [UserMessageConstants.INVITE_DETAIL_PAGE]: "/me/invitationRecord",
        [UserMessageConstants.SUB_ACCOUNT_LIST_PAGE]: "/me/subAccountManagement",
        [UserMessageConstants.PAYMENT_DETAIL_PAGE]: `/console/payment/detail${queryId}&payType=Pay`,
        [UserMessageConstants.ORGANIZATION_CERTIFICATION]: "/me/authentication",
        [UserMessageConstants.REPLY_MESSAGE_TEMPLATE]: `/public/feedback/${id}`,
        [UserMessageConstants.ORDER_DETAIL_PAGE]: `/orderCenter/myOrder/detail/${id}`,
        [UserMessageConstants.PRODUCT_GROUP_PURCHASE_LIST_PAGE]: "/public/productGroupPurchase",
        [UserMessageConstants.PRODUCT_BARGAIN_DETAIL_PAGE]: `/public/productPickUpLeak/${id}`,
        [UserMessageConstants.SAMPLE_APPLY_DETAIL_PAGE]: `/public/productConsignment/${id}`,
        [UserMessageConstants.DELIVERY_DETAIL_PAGE]: `/orderCenter/myOrder/deliveryDetail/${id}`,
        [UserMessageConstants.INVOICE_DETAIL_PAGE]: `/orderCenter/myInvoice/detail/${id}`,
        [UserMessageConstants.SUPPLIER_DETAIL_PAGE]: `/public/supplierMap/detail/${id}`,
        [UserMessageConstants.PRODUCT_GROUP_PURCHASE_DETAIL_PAGE]: `/public/productGroupPurchase/${id}`,
        [UserMessageConstants.CAMPAIGN_REGISTRATION_DETAIL_PAGE]: `/registration/myRegistrationDetail?id=${id}`
    };
    return targets[row.url as UserMessageConstants];
}

/** 我的消息：四类消息共用查询、批量已读/删除及未读角标合同。 */
export function UserMessagePage() {
    const initialType = new URLSearchParams(location.search).has("type")
        ? USER_MESSAGE_TYPE.NOTIFY.code
        : USER_MESSAGE_TYPE.TRANSACTION.code;
    const [type, setType] = useState(initialType);
    const [selected, setSelected] = useState<MessageRow[]>([]);
    const [revision, setRevision] = useState(0);
    const [counts, setCounts] = useState([0, 0, 0, 0]);
    const [listLen, setListLen] = useState(0);
    const refresh = () => {
        setSelected([]);
        setRevision(value => value + 1);
    };
    /** 未读数量独立失败不阻断列表，用户仍可查看消息。 */
    useEffect(() => {
        void Promise.all([userMessageService.getMsgNum(), notificationService.getMsgNum()])
            .then(([messages, notify]) =>
                setCounts([messages.transaction, messages.transform, notify.unread, messages.other])
            )
            .catch(() => setCounts([0, 0, 0, 0]));
    }, [revision]);
    const tabs = [
        { value: USER_MESSAGE_TYPE.TRANSACTION.code, label: "交易" },
        { value: USER_MESSAGE_TYPE.TRANSFORM.code, label: "物流" },
        { value: USER_MESSAGE_TYPE.NOTIFY.code, label: "通知" },
        { value: USER_MESSAGE_TYPE.OTHER.code, label: "其他" }
    ];
    const currentTabIndex = tabs.findIndex(tab => tab.value === type);
    const selectedIds = (unreadOnly = false) =>
        selected
            .filter(
                row =>
                    !unreadOnly ||
                    (isNotification(row) ? row.readState : row.messageState) === USER_MESSAGE_STATE.UNREAD.code
            )
            .map(row => (isNotification(row) ? row.notification.id : row.id));
    /** 批量操作保留旧版无数据提示、确认文案和通知/业务消息双接口。 */
    const operate = (kind: "read" | "delete", all = false) => {
        const ids = selectedIds(kind === "read");
        if (!all && !ids.length) {
            void message.info(kind === "read" ? "请选择未读消息" : "请选择消息");
            return;
        }
        if (all && kind === "read" && !(counts[currentTabIndex] ?? 0)) {
            void message.info("暂无未读消息");
            return;
        }
        if (all && kind === "delete" && !listLen) {
            void message.info("暂无消息");
            return;
        }
        const content = all
            ? `是否${kind === "read" ? "已读" : "删除"}全部消息`
            : kind === "read" && type !== USER_MESSAGE_TYPE.NOTIFY.code
              ? `您确定要标记选中的${ids.length}条消息为已读吗`
              : `是否${kind === "read" ? "已读" : "删除"}选中消息`;
        Modal.confirm({
            title: "提示",
            content,
            async onOk() {
                if (type === USER_MESSAGE_TYPE.NOTIFY.code) {
                    if (all) await (kind === "read" ? notificationService.allRead() : notificationService.allDel());
                    else
                        await (kind === "read"
                            ? notificationService.batchReaded({ notificationIds: ids })
                            : notificationService.batchDel({ notificationIds: ids }));
                } else if (all) {
                    await (kind === "read" ? userMessageService.allReaded(type) : userMessageService.allDel(type));
                } else {
                    await (kind === "read"
                        ? userMessageService.batchReaded(ids, type)
                        : userMessageService.batchDel(ids, type));
                }
                void message.success(!all && kind === "read" ? "标记成功" : "操作成功");
                refresh();
            }
        });
    };
    const priceWarningKeywords = ["已达到提醒价", "达到提醒价", "达到您的价格提醒"];
    const priceWarningTarget = async (row: UserMessage) => {
        const text = `${row.content ?? ""}${row.title ?? ""}`;
        if (
            String(row.url || "").toLowerCase() !== UserMessageConstants.LIAN_YUN &&
            !priceWarningKeywords.some(word => text.includes(word))
        )
            return undefined;
        if (!row.detailId) {
            void message.error("该消息缺少价格提醒详情ID");
            return "";
        }
        try {
            const index = (await indexService.getPriceWarningIndexById(row.detailId)) as any;
            const productTypeId = index?.productType?.id ?? index?.productTypeIndex?.productTypeId;
            const date = index?.productTypeIndex?.date ?? row.sendTime?.slice(0, 10);
            const queryType =
                index?.queryType ??
                PRODUCT_TYPE_PRICE_WARNING_QUERY_TYPE.options.find(option => text.includes(option.label))?.value;
            if (!productTypeId || !date) {
                void message.error("该消息不存在");
                return "";
            }
            return `/public/sandGravelIndex/detail/${productTypeId}/${date}${queryType ? `?queryType=${queryType}` : ""}`;
        } catch {
            void message.error("价格提醒详情查询失败");
            return "";
        }
    };
    const openRow = async (row: MessageRow) => {
        if (isNotification(row)) {
            await notificationService.read({ notificationIds: [row.notification.id] });
            window.open(`/console/userMessage/notification?id=${row.notification.id}`, "_blank");
        } else {
            const warningTarget = await priceWarningTarget(row);
            const target = warningTarget === undefined ? resolveMessageTarget(row) : warningTarget;
            await userMessageService.batchReaded([row.id], type);
            if (target) {
                const exists = warningTarget !== undefined || (await userMessageService.getMsgEntity({ id: row.id }));
                if (exists) window.open(target, "_blank");
                else void message.error("该页面不存在");
            }
        }
        refresh();
    };
    const request = useMemo(
        () => async (params: PageParams) => {
            const result =
                type === USER_MESSAGE_TYPE.NOTIFY.code
                    ? await notificationService.paging({
                          page: params.page,
                          size: params.size,
                          searchParam: params.searchParam as string | undefined,
                          messageState: params.messageState as number | undefined,
                          sortKey: "publish_time",
                          sortDirection: "DESC",
                          publishPlat: ADMIN_PUBLISH_PLAT.SAND_PC.code
                      })
                    : await userMessageService.paging({
                          ...params,
                          type,
                          sortKey: "send_time",
                          sortOrder: "DESC"
                      });
            setListLen(result.list?.length || 0);
            return result;
        },
        [type, revision]
    );
    const common: ProColumn<MessageRow>[] = [
        {
            key: "state",
            title: "状态",
            width: 100,
            search: {
                key: "messageState",
                type: "select",
                placeholder: "请选择状态",
                order: 2,
                options: USER_MESSAGE_STATE.options
            },
            render: (_value, row) => {
                const state = isNotification(row) ? row.readState : row.messageState;
                return (
                    <span
                        className={
                            state === USER_MESSAGE_STATE.UNREAD.code
                                ? "message-state message-state--unread"
                                : "message-state message-state--read"
                        }
                    >
                        <i />
                        {USER_MESSAGE_STATE.getIntroByCode(state) ?? "未知状态"}
                    </span>
                );
            }
        },
        {
            key: "title",
            title: "标题内容",
            minWidth: 200,
            search: { key: "searchParam", type: "input", placeholder: "请输入标题内容搜索", order: 1 },
            render: (_value, row) => (
                <Button className="message-title-link" type="link" onClick={() => void openRow(row)}>
                    {isNotification(row) ? row.notification.title : row.title}
                </Button>
            )
        },
        {
            key: "time",
            title: "发送时间",
            width: 170,
            render: (_value, row) => {
                const time = isNotification(row) ? row.notification.publishTime : row.sendTime;
                return time ? moment(time).format("YYYY-MM-DD HH:mm:ss") : "--";
            }
        },
        {
            key: "type",
            title: "类型",
            width: 150,
            render: (_value, row) =>
                isNotification(row)
                    ? (NOTIFICATION_TYPE.getIntroByCode(row.notification.classify) ?? "--")
                    : (USER_MESSAGE_TYPE.getIntroByCode(row.type) ?? "--")
        }
    ];
    return (
        <section className="user-message-page">
            <div className="user-message-page__title">我的消息</div>
            <Tabs
                activeKey={String(type)}
                onChange={key => {
                    setType(Number(key));
                    setSelected([]);
                }}
                items={tabs.map((tab, index) => ({
                    key: String(tab.value),
                    label: (
                        <Badge count={counts[index]} size="small">
                            {tab.label}
                        </Badge>
                    )
                }))}
            />
            <div className="user-message-table">
                <ProTable<MessageRow>
                    key={`${type}-${revision}`}
                    rowKey={(row): Key => (isNotification(row) ? row.notification.id : row.id)}
                    columns={common}
                    request={request}
                    onSelectionChange={setSelected}
                    header={() => (
                        <div className="message-actions">
                            <Button type={selected.length ? "primary" : "default"} onClick={() => operate("read")}>
                                标记已读
                            </Button>
                            <Button type={selected.length ? "primary" : "default"} onClick={() => operate("delete")}>
                                删除
                            </Button>
                            <Button onClick={() => operate("read", true)}>全部已读</Button>
                            <Button onClick={() => operate("delete", true)}>全部删除</Button>
                        </div>
                    )}
                />
            </div>
        </section>
    );
}
